/**
 * app-version — weet een tab dat hij een oude bundle draait?
 *
 * Office-tabs leven dagen (vaak verborgen op een tweede scherm) en laden nooit
 * zelf een nieuwe bundle. Na een softphone-deploy bleef de telefoon-eigenaar
 * (leader-tab) daardoor drie keer op de oude code hangen: de fix stond live,
 * maar de tab die de SIP-registratie doet had hem niet (06-07, 07-07, 14-09).
 *
 * Werking:
 *  - `__APP_VERSION__` is de git-sha waarop deze bundle gebouwd is
 *    (vite.config.ts). `/version.json` komt uit de deploy die het prod-alias
 *    nu bedient. Verschillen ze, dan is er een nieuwe versie.
 *  - Elke 5 min, bij tab-focus en bij zichtbaar worden wordt gecheckt.
 *  - Zichtbare tab: banner met "Herladen" (AppUpdateBanner). Nooit vanzelf,
 *    de gebruiker kan midden in een formulier zitten.
 *  - Verborgen tab zonder levend gesprek: na HIDDEN_RELOAD_MS vanzelf
 *    herladen. Precies die tab is de klasse (niemand kijkt, wel de telefoon),
 *    en het tijdvenster beschermt een tab die iemand net even wegklikte.
 *  - Nooit herladen zolang er ergens een gesprek loopt of rinkelt; de
 *    phone-state is cross-tab, dus een niet-leader-tab weet dat ook.
 */
import { getCalls } from './telephony/phone-state';

export const CURRENT_VERSION: string = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'onbekend';

const POLL_MS = 5 * 60 * 1000;
const HIDDEN_RELOAD_MS = 10 * 60 * 1000;
const HIDDEN_TICK_MS = 60 * 1000;

type Listener = (v: VersionState) => void;

export interface VersionState {
  current: string;
  served: string | null;
  updateAvailable: boolean;
  checkedAt: number | null;
}

let state: VersionState = { current: CURRENT_VERSION, served: null, updateAvailable: false, checkedAt: null };
const listeners = new Set<Listener>();
let started = false;
let hiddenSince: number | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let hiddenTimer: ReturnType<typeof setInterval> | null = null;
let reloading = false;

function notify(): void {
  for (const l of listeners) l(state);
}

export function getVersionState(): VersionState {
  return state;
}

export function subscribeVersion(l: Listener): () => void {
  listeners.add(l);
  l(state);
  return () => {
    listeners.delete(l);
  };
}

/** Rinkelend, uitgaand of actief gesprek in welke tab dan ook. */
export function heeftLevendGesprek(): boolean {
  return getCalls().some((c) => c.state !== 'idle' && c.state !== 'ended');
}

export async function fetchServedVersion(fetcher: typeof fetch = fetch): Promise<string | null> {
  try {
    const res = await fetcher(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const body = (await res.json()) as { version?: unknown };
    return typeof body.version === 'string' && body.version !== '' ? body.version : null;
  } catch {
    return null;
  }
}

/**
 * Zuivere beslissing, los van timers: is er een update en verschilt de
 * servende versie van de eigen? Een onbekende servende versie (netwerk,
 * dev-server zonder version.json) is nooit een update.
 */
export function isUpdate(current: string, served: string | null): boolean {
  return served != null && served !== current && !current.startsWith('dev-');
}

export async function checkVersion(fetcher: typeof fetch = fetch): Promise<VersionState> {
  const served = await fetchServedVersion(fetcher);
  state = {
    current: CURRENT_VERSION,
    served,
    // Eén keer gezien blijft gezien: een tijdelijke netwerkfout maakt een
    // eerder gevonden update niet ongedaan.
    updateAvailable: state.updateAvailable || isUpdate(CURRENT_VERSION, served),
    checkedAt: Date.now(),
  };
  notify();
  return state;
}

/**
 * Herladen als het veilig is. Geeft terug of het herladen ingezet is; de
 * banner gebruikt dezelfde regel, zodat de knop en de automaat nooit van
 * mening verschillen ([[feedback_gate_en_uitvoerder_delen_dezelfde_regels]]).
 */
export function reloadAlsVeilig(): boolean {
  if (reloading) return true;
  if (heeftLevendGesprek()) return false;
  reloading = true;
  window.location.reload();
  return true;
}

export function mayAutoReloadHidden(now: number = Date.now()): boolean {
  if (!state.updateAvailable) return false;
  if (typeof document === 'undefined' || !document.hidden) return false;
  if (hiddenSince == null || now - hiddenSince < HIDDEN_RELOAD_MS) return false;
  return !heeftLevendGesprek();
}

function onVisibility(): void {
  if (document.hidden) {
    hiddenSince = hiddenSince ?? Date.now();
  } else {
    hiddenSince = null;
    void checkVersion();
  }
}

export function startVersionWatch(): () => void {
  if (started) return () => {};
  started = true;
  hiddenSince = document.hidden ? Date.now() : null;
  // Eerste check kort na start: een tab die na dagen wakker wordt uit
  // bfcache/slaap is anders pas na vijf minuten bij.
  const first = setTimeout(() => void checkVersion(), 15_000);
  pollTimer = setInterval(() => void checkVersion(), POLL_MS);
  hiddenTimer = setInterval(() => {
    if (mayAutoReloadHidden()) reloadAlsVeilig();
  }, HIDDEN_TICK_MS);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('focus', onVisibility);
  return () => {
    started = false;
    clearTimeout(first);
    if (pollTimer) clearInterval(pollTimer);
    if (hiddenTimer) clearInterval(hiddenTimer);
    pollTimer = null;
    hiddenTimer = null;
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('focus', onVisibility);
  };
}

/** Alleen voor tests. */
export function __resetVersionStateForTests(): void {
  state = { current: CURRENT_VERSION, served: null, updateAvailable: false, checkedAt: null };
  hiddenSince = null;
  reloading = false;
  started = false;
}
export function __setHiddenSinceForTests(ts: number | null): void {
  hiddenSince = ts;
}
